import React, { FC, useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from '../../../store'
import CustomPopup from '../../UI/Popup'
import GeneralForm from '../../Form'
import Checkbox from '../../UI/Inputs/Checkbox'
import { FormikHelpers } from 'formik'
import CustomButton from '../../UI/Buttons/CustomButton'
import { TSkipApprovalFormValues } from '../../../models/form'
import { APPROVAL_TYPE, POST_SOCIAL_STATUS, POST_STATUS } from '../../../models/post'
import {
  toggleIsSkipAprrovalModalAction,
  resetSkipApprovalAction,
  openApproverModalAction,
  updateSkipApproverAction
} from '../../../store/modal/skipApprovalPost/slice'
import { toggleIsOpenAprrovalModalAction } from '../../../store/modal/approvalPost/slice'
import { skipApprovalPostsThunk, skipApprovalPostsSocialThunk } from '../../../store/modal/skipApprovalPost/thunks'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import {
  updatePostSocialsThunk,
  savePostAsDraftAndScheduleThunk,
  startNextApprovalRoundThunk,
  getPostApprovalInfoThunk
} from '../../../store/managePost/thunks'
import { updateSkipApprovalAction } from '../../../store/managePost/slice'
import { generatePath, useParams } from 'react-router'
import { goBack, push } from 'connected-react-router'
import { AUTH_ROUTE } from '../../../pages/App/routes'
import Roles from '../../../models/roles'
import { getProjectUsersByRoleThunk } from '../../../store/project/thunks'

import './SkipApprovalModal.scss'
import { SETTING_NAME } from '../../../store/project/types'
import { TApprovalPosts } from '../../../store/modal/approvalPost/types'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import { postApprovalPostsThunk } from '../../../store/modal/approvalPost/thunks'
import { useToasts } from 'react-toast-notifications'

interface SkipApprovalMoalInterface {}

const SkipApprovalModal: FC<SkipApprovalMoalInterface> = () => {
  const dispatch = useDispatch()
  const {
    postId,
    project,
    postSocials,
    isSkipApprovalModalOpen,
    inProgress,
    inProgressPost,
    skippedApprovals,
    selectedPostSocialId,
    postSocialsApprovalPlan,
    user
  } = useSelector((state) => ({
    postId: state.managePost.post.id,
    inProgressPost: state.managePost.loaders.post.save,
    project: state.project.currentProject,
    postSocials: state.managePost.postSocials,
    isSkipApprovalModalOpen: state.skipApproval.is_skip_approval_modal_open,
    inProgress: state.skipApproval.inProgress,
    skippedApprovals: state.skipApproval.skipped_approvals,
    selectedPostSocialId: state.managePost.selectedPostSocialId,
    postSocialsApprovalPlan: state.managePost.approval.post_socials,
    user: state.auth.user
  }))
  const [checkboxValue, setCheckboxValue] = useState<Array<string>>([])
  const [isContentManager, setIsContentManager] = useState<boolean>(false);
  const { addToast } = useToasts()
  useEffect(() => {
    setCheckboxValue(skippedApprovals)
  }, [skippedApprovals])

  useEffect(() => {
      dispatch(getProjectUsersByRoleThunk())
  }, [])

  const isExistRevisionsPostSocial: boolean = useMemo(() => {
    const postSocial = postSocials.find(({ id }) => id === selectedPostSocialId)!
    if (postSocial) {
      return postSocial?.status === POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION
    }
    return false
  }, [selectedPostSocialId, postSocials])

  const approversList = useMemo(() => project.projectUsers.filter(
    ({ role ,setting}) =>
    (role === Roles.EXTERNAL_APPROVER ) 
  ),[project.projectUsers])

  const internalApproversList = useMemo(() => project.projectUsers.filter(
    ({ role ,setting}) =>
    (role === Roles.INTERNAL_APPROVER ) 
  ),[project.projectUsers])  

  useEffect(() => {
    const role_name = user?.projects?.find(v=>v.id === project.id)?.setting.find(v=>v.name === SETTING_NAME.CONTENT_MANAGER && v.value === 'true')?.name;
    if(role_name !== null && role_name === 'content-manager'){
      setIsContentManager(true);
    }
  }, [user]);

  const isAdmin = useMemo(() => (user?.agency_role === Roles.AGENCY_ADMIN || user?.agency_role === Roles.SUPER_ADMIN), [user])

  const isContentCreator = useMemo(() => user?.agency_role === Roles.CONTENT_CREATOR, [user])

  const onSubmit = async () => {
    if (checkboxValue.length > 0) {
      if (
        (checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) &&
        checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL)) || (approversList.length === 0) || 
        !(isContentManager || isAdmin) && internalApproversList.length > 0 && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) || 
        ((isAdmin || isContentManager) && ((checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && (approversList.length === 0 || approversList.length > 0)) || (checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && (internalApproversList.length === 0 || internalApproversList.length > 0))))
      ) {
        let result
        if (!postId) {
          result = await savePostAsDraft()
        } else {
          result = await dispatch(updatePostSocialsThunk())
        }
        if (isFulfilled(result)) {
            let response;
            let responseSkip;
            if(isExistRevisionsPostSocial){
              const masterContentSocial = postSocialsApprovalPlan.find(
                (socialPlan) => socialPlan.is_master_content
              )    
              if(!(isContentManager || isAdmin) && internalApproversList.length > 0 && approversList.length === 0 || 
                  !(isContentManager || isAdmin) && internalApproversList.length > 0 && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL)) {
                const skipExtlApprovals = [ APPROVAL_TYPE.TYPE_EXTERNAL ];
                const payload = {
                  post_social_id: masterContentSocial?masterContentSocial.id:selectedPostSocialId,
                  skipped_approvals: skipExtlApprovals
                }
                responseSkip = await dispatch(skipApprovalPostsSocialThunk(payload))
                if(isFulfilled(responseSkip)){
                  skipSendToApproval(postId === 0 ? result.payload.id : postId);
                  response = responseSkip;
                }
              } else if((((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && approversList.length > 0) || 
                ((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && internalApproversList.length > 0)) && 
                !((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL))) {
                let skipApprovals;
                if(checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && approversList.length > 0){
                  skipApprovals = [APPROVAL_TYPE.TYPE_INTERNAL];
                } else if(checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && internalApproversList.length > 0) {
                  skipApprovals = [APPROVAL_TYPE.TYPE_EXTERNAL];
                }
                const payload: any = {
                  post_social_id: masterContentSocial?masterContentSocial.id:selectedPostSocialId,
                  skipped_approvals: skipApprovals
                }
                responseSkip = await dispatch(skipApprovalPostsSocialThunk(payload))
                if(isFulfilled(responseSkip)){
                  skipSendToApproval(postId === 0 ? result.payload.id : postId);
                  response = responseSkip;
                }
              } else {
                const payload = {
                  post_social_id: masterContentSocial?masterContentSocial.id:selectedPostSocialId,
                  skipped_approvals: checkboxValue
                }
                response = await dispatch(skipApprovalPostsSocialThunk(payload))
              }
            } else if((!(isContentManager || isAdmin) && internalApproversList.length > 0 && approversList.length === 0) || 
              (!(isContentManager || isAdmin) && internalApproversList.length > 0 && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL))) {
                const skipExtlApprovals = [ APPROVAL_TYPE.TYPE_EXTERNAL ];
                const payload = {
                  post_id: postId === 0 ? result.payload.id : postId,
                  skipped_approvals: skipExtlApprovals
                }

                responseSkip = await dispatch(skipApprovalPostsThunk(payload))
                if(isFulfilled(responseSkip)){
                  skipSendToApproval(result.payload.id);
                  response = responseSkip;
                }
            } else if((((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && approversList.length > 0) || 
            ((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && internalApproversList.length >= 0)) && 
            !((isContentManager || isAdmin) && checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL))) {
              let skipApprovals;
              if(checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) && approversList.length > 0){
                skipApprovals = [APPROVAL_TYPE.TYPE_INTERNAL];
              } else if(checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && internalApproversList.length >= 0) {
                skipApprovals = [APPROVAL_TYPE.TYPE_EXTERNAL];
              }
              const payload: any = {
                post_id: postId === 0 ? result.payload.id : postId,
                skipped_approvals: skipApprovals
              }
              responseSkip = await dispatch(skipApprovalPostsThunk(payload))
              if(isFulfilled(responseSkip)){
                skipSendToApproval(result.payload.id);
                response = responseSkip;
              }
            } else {
              const payload = {
                post_id: postId === 0 ? result.payload.id : postId,
                skipped_approvals: checkboxValue
              }
              response = await dispatch(skipApprovalPostsThunk(payload))
            }
          
          if (isFulfilled(response)) {
            const { post_socials, post_status } = response.payload.data
            await dispatch(updateSkipApprovalAction({ post_status, post_socials }))
            await dispatch(toggleIsSkipAprrovalModalAction())
            await dispatch(resetSkipApprovalAction())
          }
        }
      } else {
        
        await dispatch(toggleIsSkipAprrovalModalAction())
        await dispatch(updateSkipApproverAction(checkboxValue))
        dispatch(openApproverModalAction(true))
      }
    }

    // let result
    // if (!postId) {
    //   result = await savePostAsDraft()
    // } else {
    //   result = await dispatch(updatePostSocialsThunk())
    // }
    // if (isFulfilled(result)) {
    //   const payload = {
    //     post_id: postId === 0 ? result.payload.id : postId,
    //     skipped_approvals: checkboxValue
    //   }
    //   const response = await dispatch(skipApprovalPostsThunk(payload))
    //   if (isFulfilled(response)) {
    //   if (
    //     checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL) &&
    //     checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL)
    //   ) {
    //     const { post_socials, post_status } = response.payload.data
    //     dispatch(updateSkipApprovalAction({ post_status, post_socials }))
    //     dispatch(toggleIsSkipAprrovalModalAction())
    //     dispatch(resetSkipApprovalAction())
    //   } else {
    //     dispatch(toggleIsSkipAprrovalModalAction())
    //     dispatch(openApproverModalAction(true))
    //     dispatch(openApproverModalAction(false))
    //     // dispatch(toggleIsOpenAprrovalModalAction())
    //   }

    //   }
    // }
  } 

  const skipSendToApproval = (postId: number) => {
    let postsIds: number[] = [];
    postsIds.push(postId)
    const batchPayload: TApprovalPosts = {
      posts_ids: postsIds
    }

    batchPayload.concrete_internal_approver_id = []
    batchPayload.concrete_external_approver_id = []

    sendToApproval(batchPayload)
  }

  const sendToApproval = async (payload:TApprovalPosts)=>{
    if(isExistRevisionsPostSocial){

      const masterContentSocialApproval = postSocialsApprovalPlan.find(
        (socialApproval) => socialApproval.is_master_content
      )

      const  responseStartApprovalFlow = await dispatch(startNextApprovalRoundThunk({ ...payload,post_social_id:masterContentSocialApproval?masterContentSocialApproval.id:selectedPostSocialId}))
      if (isFulfilled(responseStartApprovalFlow)) {
        await dispatch(getPostApprovalInfoThunk(payload.posts_ids[0]))
        addToast(TOAST_MESSAGE.POST_SENT_FOR_APPROVAL, { appearance: 'success' })
      } else if (isRejected(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      }
    }else{
      const responseStartApprovalFlow = await dispatch(postApprovalPostsThunk(payload))

      if (isFulfilled(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.POST_SENT_FOR_APPROVAL, { appearance: 'success' })
        dispatch(resetSkipApprovalAction())
      } else if (isRejected(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      }
    }
  }

  const savePostAsDraft = async () => {
    const savePostResponse = await dispatch(savePostAsDraftAndScheduleThunk())
    if (isFulfilled(savePostResponse)) {
      await dispatch(
        push(
          generatePath(AUTH_ROUTE.MANAGE_POST, {
            projectId: project.id,
            social: postSocials[0].social_network
          }) + `?postId=${savePostResponse.payload.id}`
        )
      )
    }
    return savePostResponse
  }

  const handleChange = (checked: boolean, type: string) => {
    const value = [...checkboxValue]
    if (checked) {
      value.push(type)
    } else {
      const index = value.indexOf(type)
      if (index > -1) {
        value.splice(index, 1)
      }
    }
    setCheckboxValue(value)
  }

  const close = () => {
    dispatch(resetSkipApprovalAction())
    dispatch(toggleIsSkipAprrovalModalAction())
  }

  const isSkipScheduleButtonDisable = useMemo(() => 
    (internalApproversList.length === 0 && approversList.length > 0 && !(isContentManager || isAdmin))
  , [internalApproversList, approversList]);

  const isAlertShow = useMemo(() => 
    (internalApproversList.length === 0 && approversList.length === 0) || (internalApproversList.length === 0 && approversList.length > 0)
  , [internalApproversList, approversList])

  const haveContentManager = useMemo(
    () => 
      project.projectUsers
        .filter(
          ({ role ,setting}) =>
          (!!setting.find((val) => val.name === SETTING_NAME.CONTENT_MANAGER && val.value  == 'true'))
        )
    , [project]
  )

  const scheduleButtonName = useMemo(() => 
    (internalApproversList.length > 0 && approversList.length === 0 && !(isContentManager || isAdmin)) || 
    (isAdmin && haveContentManager.length === 0 && internalApproversList.length === 0 && approversList.length === 0)
  , [internalApproversList, approversList]);

  return (
    <>
      <CustomPopup
        open={isSkipApprovalModalOpen}
        // buttonClose={closeInviteApprovalModal}
        closeOnDocumentClick={false}
        className='skip-to-approval'
      >
        <div className='skip-to-approval__wrapper'>
          {/* { internalApproversList.length === 0 && approversList.length === 0 && (isAdmin || isContentManager) ? 
            <p className='skip-to-approval__subtext'>Are you sure to schedule the post?</p> : 
          <> */}
            <div className='skip-to-approval__title'>{ scheduleButtonName ? 'Schedule' : 'Skip Approval' }</div>
            <p className='skip-to-approval__subtext'>{ !scheduleButtonName ? 'Select which approvers to skip' : ''}</p>
          {/* </>
          } */}
          <div className='skip-to-approval__list'>
            { (internalApproversList.length === 0 && approversList.length === 0 && !isContentManager && !isAdmin)
            && <div className='approver-error'><div className="approver-error__icon"></div>Please add internal and external(optional) approver. contact Admin</div> }
            { (isContentManager || (isAdmin && (haveContentManager.length > 0 || internalApproversList.length > 0)))? 
            <>
              <div className='skip-to-approval__input'>
                <div className='skip-to-approval__checklist'>
                  <Checkbox
                    label={'Internal approvers'}
                    name={APPROVAL_TYPE.TYPE_INTERNAL}
                    checked={checkboxValue.includes(APPROVAL_TYPE.TYPE_INTERNAL)}
                    onClick={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(checked, APPROVAL_TYPE.TYPE_INTERNAL)
                    }
                  />
                </div>
              </div>
            </> : null }
            { approversList.length > 0 ? 
              <>
                <div className='skip-to-approval__input'>
                  <div className='skip-to-approval__checklist'>
                    <Checkbox
                      label={'External approvers'}
                      name={APPROVAL_TYPE.TYPE_EXTERNAL}
                      checked={checkboxValue.includes(APPROVAL_TYPE.TYPE_EXTERNAL)}
                      onClick={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange(checked, APPROVAL_TYPE.TYPE_EXTERNAL)
                      }
                    />
                  </div>
                </div>
                { (internalApproversList.length === 0 && approversList.length > 0 && !(isContentManager || isAdmin))
                  && <div className='approver-error'><div className="approver-error__icon"></div>Please add internal approver. contact Admin</div> }
              </> : null }
            <div className='skip-to-approval__actions'>
              <CustomButton onClick={close} type='button' button='secondary'>
                Cancel
              </CustomButton>
              <CustomButton
                disabled={ isSkipScheduleButtonDisable ? true : checkboxValue.length === 0 }
                inProgress={inProgress || inProgressPost}
                button='primary'
                type='button'
                onClick={onSubmit}
              >
                { scheduleButtonName ? 'Schedule' : 'Skip and Schedule' }
              </CustomButton>
            </div>
            { (!(isContentManager || isAdmin) && !isAlertShow) && <div className='approver-error'><div className="approver-error__icon"></div>Unable to skip internal approver due to your permissions. Please contact your administrator.</div>}
          </div>
        </div>
      </CustomPopup>
    </>
  )
}

export default SkipApprovalModal
